import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  IconButton,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveIcon from "@mui/icons-material/Save";
import TopNavBar from "../components/TopNavBar";
import { useParams } from "react-router-dom";
import { getDevices } from "../api/devices";
import { getRoomsWithHierarchy } from "../api/rooms";
import {
  getServicePlanTemplateById,
  getServicePlanTemplates,
} from "../api/servicePlans";
import { getRouteTemplates } from "../api/routeTemplates";
import {
  createDeviceServicePlan,
  getDeviceServicePlanById,
  getDeviceServicePlans,
  replaceDevicePlanTaskOverrides,
  updateDeviceServicePlan,
} from "../api/devicePlans";

const WEEKDAY_OPTIONS = [
  { value: "1", label: "Mon" },
  { value: "2", label: "Tue" },
  { value: "3", label: "Wed" },
  { value: "4", label: "Thu" },
  { value: "5", label: "Fri" },
  { value: "6", label: "Sat" },
  { value: "7", label: "Sun" },
];

const WEEKDAY_LABEL_BY_VALUE = new Map(
  WEEKDAY_OPTIONS.map((option) => [option.value, option.label]),
);

const RECURRENCE_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "every_n_days", label: "Every N days" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

const TRIGGER_MODE_OPTIONS = [
  { value: "schedule", label: "Schedule" },
  { value: "condition", label: "Condition" },
  { value: "hybrid", label: "Hybrid" },
];

const splitIntoColumns = (items, columnCount = 2) => {
  const perColumn = Math.ceil(items.length / columnCount) || 0;
  const columns = [];
  for (let i = 0; i < columnCount; i += 1) {
    const start = i * perColumn;
    columns.push(items.slice(start, start + perColumn));
  }
  return columns;
};

function normalizeDevices(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.devices)) return payload.devices;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function normalizeRooms(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.rooms)) return payload.rooms;
  return [];
}

function todayDateString() {
  return new Date().toISOString().slice(0, 10);
}

function buildOverrideDraft(override) {
  return {
    id: String(override.pk_id || `${override.task_id}-${Math.random()}`),
    taskId: String(override.task_id || ""),
    recurrenceTypeOverride: override.recurrence_type_override || "",
    intervalNOverride:
      override.interval_n_override != null
        ? String(override.interval_n_override)
        : "",
    dayOfWeekOverride:
      override.day_of_week_override != null &&
      String(override.day_of_week_override) !== ""
        ? String(override.day_of_week_override)
            .split(",")
            .map((value) => value.trim())
            .filter(Boolean)
        : [],
    dayOfMonthOverride:
      override.day_of_month_override != null
        ? String(override.day_of_month_override)
        : "",
    anchorDateOverride: override.anchor_date_override
      ? String(override.anchor_date_override).slice(0, 10)
      : "",
    triggerModeOverride: override.trigger_mode_override || "",
    mandatoryOverride:
      override.mandatory_override == null
        ? ""
        : String(Number(override.mandatory_override)),
    enabledOverride:
      override.enabled_override == null
        ? ""
        : String(Number(override.enabled_override)),
    notes: override.notes || "",
  };
}

function toIntOrNull(value) {
  if (value === "" || value == null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

// A weekly task scheduled on multiple weekdays is stored as one
// service_plan_template_tasks row per weekday (same task_id). Collapse those
// into a single entry per task, keeping every scheduled weekday for display.
function groupTemplateTasksByTaskId(tasks = []) {
  const map = new Map();

  tasks.forEach((task) => {
    const id = String(task.task_id || task.pk_id || "");
    if (!id) return;

    if (!map.has(id)) {
      map.set(id, { ...task, day_of_week_list: [] });
    }

    const entry = map.get(id);
    const dayOfWeek = task.day_of_week;
    if (dayOfWeek != null && !entry.day_of_week_list.includes(dayOfWeek)) {
      entry.day_of_week_list.push(dayOfWeek);
    }
  });

  return [...map.values()];
}

function formatTaskSummary(task) {
  if (!task) return "Unknown task";

  const recurrenceType = task.recurrence_type || task.recurrenceType || "daily";
  const intervalN = task.interval_n || task.intervalN;
  const dayOfMonth = task.day_of_month || task.dayOfMonth;
  const dayOfWeekList =
    Array.isArray(task.day_of_week_list) && task.day_of_week_list.length > 0
      ? task.day_of_week_list
      : [task.day_of_week || task.dayOfWeek].filter((value) => value != null);
  const anchorDate = task.anchor_date || task.anchorDate;
  const triggerMode = task.trigger_mode || task.triggerMode || "schedule";
  const mandatory = task.mandatory;
  const active = task.active;

  const weekdayNames = dayOfWeekList
    .slice()
    .sort((a, b) => a - b)
    .map(
      (value) => WEEKDAY_LABEL_BY_VALUE.get(String(value)) || `Day ${value}`,
    );

  const recurrenceLabel =
    recurrenceType === "every_n_days"
      ? intervalN
        ? `Every ${intervalN} day(s)`
        : "Every N days"
      : recurrenceType === "weekly"
        ? weekdayNames.length > 0
          ? `Weekly (${weekdayNames.join(", ")})`
          : "Weekly"
        : recurrenceType === "monthly"
          ? dayOfMonth
            ? `Monthly (day ${dayOfMonth})`
            : "Monthly"
          : recurrenceType === "daily"
            ? "Daily"
            : recurrenceType;

  const parts = [recurrenceLabel, `Trigger: ${triggerMode}`];
  if (anchorDate) parts.push(`Anchor: ${String(anchorDate).slice(0, 10)}`);
  if (mandatory !== undefined) parts.push(mandatory ? "Mandatory" : "Optional");
  if (active !== undefined) parts.push(active ? "Active" : "Inactive");

  return parts.join(" · ");
}

export default function ConfigureDevicePlanPage() {
  const { targetType = "device", targetId } = useParams();
  const isRoomTarget = targetType === "room";

  const [device, setDevice] = useState(null);
  const [plans, setPlans] = useState([]);
  const [selectedServiceTypeId, setSelectedServiceTypeId] = useState("");
  const [serviceTemplates, setServiceTemplates] = useState([]);
  const [routeTemplates, setRouteTemplates] = useState([]);
  const [templateTasks, setTemplateTasks] = useState([]);
  const [overrideDraft, setOverrideDraft] = useState([]);
  const [overridesLoading, setOverridesLoading] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(null);

  const [form, setForm] = useState({
    active: true,
    servicePlanTemplateId: "",
    routeTemplateId: "",
    startsOn: todayDateString(),
    endsOn: "",
    timezone: "Europe/Copenhagen",
  });

  useEffect(() => {
    if (!targetId) return;

    setLoading(true);
    setError(null);

    Promise.all([
      isRoomTarget ? getRoomsWithHierarchy() : getDevices(),
      getServicePlanTemplates(),
      getRouteTemplates(),
      getDeviceServicePlans(
        isRoomTarget ? { roomId: targetId } : { deviceId: targetId },
      ),
    ])
      .then(
        ([
          targetListData,
          servicePlansData,
          routeTemplatesData,
          devicePlansData,
        ]) => {
          const allTargets = isRoomTarget
            ? normalizeRooms(targetListData)
            : normalizeDevices(targetListData);
          const selectedTarget = allTargets.find(
            (t) => String(t.pk_id) === String(targetId),
          );

          if (!selectedTarget) {
            setError(
              isRoomTarget ? "Room was not found" : "Device was not found",
            );
            return;
          }

          setDevice(selectedTarget);
          setServiceTemplates(servicePlansData?.templates || []);
          setRouteTemplates(routeTemplatesData?.templates || []);
          setPlans(
            Array.isArray(devicePlansData?.plans) ? devicePlansData.plans : [],
          );
        },
      )
      .catch((err) => setError(err.message || "Failed to load plan assignment"))
      .finally(() => setLoading(false));
  }, [targetId, isRoomTarget]);

  const customerServiceTemplates = useMemo(() => {
    if (!device) return serviceTemplates;

    const customerId = Number(device.customer_id || device.customer_pk_id || 0);
    return serviceTemplates.filter((tpl) => {
      const tplCustomerId =
        tpl.customer_id == null ? null : Number(tpl.customer_id);
      return tplCustomerId == null || tplCustomerId === customerId;
    });
  }, [serviceTemplates, device]);

  const filteredRouteTemplates = useMemo(() => {
    if (!device) return routeTemplates;

    const customerId = Number(device.customer_id || device.customer_pk_id || 0);
    return routeTemplates.filter((tpl) => {
      const tplCustomerId =
        tpl.customer_id == null ? null : Number(tpl.customer_id);
      return tplCustomerId == null || tplCustomerId === customerId;
    });
  }, [routeTemplates, device]);

  // One tab per distinct service type found in the customer's templates,
  // so a device can be assigned a separate plan per type (e.g. remote + on-site).
  // Also include any service type the device already has a plan for, even if
  // no template currently matches its customer scope (e.g. the device moved
  // to a different customer/location after the plan was created) - otherwise
  // that plan's tab disappears and it can never be reached to deactivate it.
  const serviceTypeOptions = useMemo(() => {
    const map = new Map();
    // Room-anchored (manual cleaning) plans are on-site only - there's no
    // device to service remotely.
    const isRemoteServiceType = (name) => /remote/i.test(name || "");

    customerServiceTemplates.forEach((tpl) => {
      const id = String(tpl.service_type_id || "");
      if (!id || map.has(id)) return;
      const name = tpl.service_type_name || `Service type #${id}`;
      if (isRoomTarget && isRemoteServiceType(name)) return;
      map.set(id, { id, name });
    });
    plans.forEach((plan) => {
      const id = String(plan.service_type_id || "");
      if (!id || map.has(id)) return;
      const name = plan.service_type_name || `Service type #${id}`;
      if (isRoomTarget && isRemoteServiceType(name)) return;
      map.set(id, { id, name });
    });
    return [...map.values()];
  }, [customerServiceTemplates, plans, isRoomTarget]);

  useEffect(() => {
    if (!selectedServiceTypeId && serviceTypeOptions.length > 0) {
      setSelectedServiceTypeId(serviceTypeOptions[0].id);
    }
  }, [serviceTypeOptions, selectedServiceTypeId]);

  const existingPlan = useMemo(() => {
    if (!selectedServiceTypeId) return null;
    return (
      plans.find(
        (plan) =>
          String(plan.service_type_id || "") === String(selectedServiceTypeId),
      ) || null
    );
  }, [plans, selectedServiceTypeId]);

  const filteredServiceTemplates = useMemo(() => {
    const base = !selectedServiceTypeId
      ? customerServiceTemplates
      : customerServiceTemplates.filter(
          (tpl) =>
            String(tpl.service_type_id || "") === String(selectedServiceTypeId),
        );

    // Keep the currently assigned template selectable even if it no longer
    // matches the device's customer scope (e.g. device moved to a different
    // customer/location) - otherwise the dropdown renders blank and the
    // existing plan can't be edited/deactivated through this form.
    const existingTemplateId = existingPlan?.service_plan_template_id;
    if (
      existingTemplateId &&
      !base.some((tpl) => String(tpl.pk_id) === String(existingTemplateId))
    ) {
      return [
        {
          pk_id: existingTemplateId,
          template_name: `${existingPlan.service_plan_template_name || `Template #${existingTemplateId}`} (no longer in scope)`,
          service_type_id: existingPlan.service_type_id,
        },
        ...base,
      ];
    }

    return base;
  }, [customerServiceTemplates, selectedServiceTypeId, existingPlan]);

  // Reset the form to the plan for the currently selected service type
  // (or a blank draft if the device has no plan of that type yet).
  useEffect(() => {
    if (existingPlan) {
      setForm({
        active: Number(existingPlan.active) === 1,
        servicePlanTemplateId: String(
          existingPlan.service_plan_template_id || "",
        ),
        routeTemplateId: String(existingPlan.route_template_id || ""),
        startsOn:
          String(existingPlan.starts_on || "").slice(0, 10) ||
          todayDateString(),
        endsOn: existingPlan.ends_on
          ? String(existingPlan.ends_on).slice(0, 10)
          : "",
        timezone: existingPlan.timezone || "Europe/Copenhagen",
      });
    } else {
      setForm({
        active: true,
        servicePlanTemplateId: "",
        routeTemplateId: "",
        startsOn: todayDateString(),
        endsOn: "",
        timezone: "Europe/Copenhagen",
      });
    }
  }, [existingPlan]);

  useEffect(() => {
    const templateId = form.servicePlanTemplateId;
    if (!templateId) {
      setTemplateTasks([]);
      setOverrideDraft([]);
      return;
    }

    getServicePlanTemplateById(templateId)
      .then((data) => {
        const tasks = Array.isArray(data?.template?.tasks)
          ? data.template.tasks
          : [];
        setTemplateTasks(tasks);
        console.log("Service plan template tasks loaded", tasks);
      })
      .catch(() => setTemplateTasks([]));
  }, [form.servicePlanTemplateId]);

  useEffect(() => {
    if (!existingPlan?.pk_id) {
      setOverrideDraft([]);
      return;
    }

    setOverridesLoading(true);
    getDeviceServicePlanById(existingPlan.pk_id)
      .then((data) => {
        const overrides = Array.isArray(data?.plan?.task_overrides)
          ? data.plan.task_overrides
          : [];
        setOverrideDraft(overrides.map(buildOverrideDraft));
      })
      .catch(() => setOverrideDraft([]))
      .finally(() => setOverridesLoading(false));
  }, [existingPlan?.pk_id]);

  // Rooms have no device: robot-specific tasks are never applicable there.
  // Also collapse multi-weekday rows (same task_id repeated per weekday)
  // into one entry each, so the task list/checklist doesn't show duplicates.
  const eligibleTemplateTasks = useMemo(() => {
    const base = isRoomTarget
      ? templateTasks.filter((task) => !task.requires_device)
      : templateTasks;
    return groupTemplateTasksByTaskId(base);
  }, [templateTasks, isRoomTarget]);

  const taskOptions = useMemo(() => {
    const map = new Map();
    eligibleTemplateTasks.forEach((task) => {
      const id = String(task.task_id || task.pk_id || "");
      if (!id) return;
      if (!map.has(id)) {
        map.set(id, task.task_name || `Task #${id}`);
      }
    });
    return [...map.entries()].map(([id, name]) => ({ id, name }));
  }, [eligibleTemplateTasks]);

  const taskById = useMemo(() => {
    const map = new Map();
    eligibleTemplateTasks.forEach((task) => {
      const id = String(task.task_id || task.pk_id || "");
      if (id) {
        map.set(id, task);
      }
    });
    return map;
  }, [eligibleTemplateTasks]);

  // Room tasks are opt-in: unchecked (no override row) means not allocated
  // to this room. The template only defines which tasks are selectable here;
  // the actual schedule is always configured below in the override panel,
  // never inherited from the template's own recurrence.
  const toggleRoomTaskEnabled = (taskId) => {
    setOverrideDraft((prev) => {
      const existingIndex = prev.findIndex(
        (row) => String(row.taskId) === String(taskId),
      );

      if (existingIndex !== -1) {
        return prev.filter((row) => String(row.taskId) !== String(taskId));
      }

      return [
        ...prev,
        {
          id: `room-${taskId}`,
          taskId: String(taskId),
          recurrenceTypeOverride: "",
          intervalNOverride: "",
          dayOfWeekOverride: [],
          dayOfMonthOverride: "",
          anchorDateOverride: "",
          triggerModeOverride: "",
          mandatoryOverride: "",
          enabledOverride: "1",
          notes: "",
        },
      ];
    });
  };

  const updateOverrideRow = (rowId, patch) => {
    setOverrideDraft((prev) =>
      prev.map((row) => (row.id === rowId ? { ...row, ...patch } : row)),
    );
  };

  const removeOverrideRow = (rowId) => {
    setOverrideDraft((prev) => prev.filter((row) => row.id !== rowId));
  };

  const addOverrideRow = () => {
    const used = new Set(overrideDraft.map((row) => String(row.taskId || "")));
    const firstUnused = taskOptions.find(
      (option) => !used.has(String(option.id)),
    );

    setOverrideDraft((prev) => [
      ...prev,
      {
        id: `new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        taskId: firstUnused?.id || "",
        recurrenceTypeOverride: "",
        intervalNOverride: "",
        dayOfWeekOverride: [],
        dayOfMonthOverride: "",
        anchorDateOverride: "",
        triggerModeOverride: "",
        mandatoryOverride: "",
        enabledOverride: "",
        notes: "",
      },
    ]);
  };

  const buildOverridePayload = () => {
    return overrideDraft
      .filter((row) => row.taskId)
      .map((row) => {
        const recurrence = row.recurrenceTypeOverride || null;
        return {
          taskId: toIntOrNull(row.taskId),
          recurrenceTypeOverride: recurrence,
          intervalNOverride:
            recurrence === "every_n_days"
              ? toIntOrNull(row.intervalNOverride)
              : null,
          dayOfWeekOverride:
            recurrence === "weekly" && Array.isArray(row.dayOfWeekOverride)
              ? row.dayOfWeekOverride
                  .map((value) => toIntOrNull(value))
                  .filter((value) => value != null)
                  .join(",") || null
              : null,
          dayOfMonthOverride:
            recurrence === "monthly"
              ? toIntOrNull(row.dayOfMonthOverride)
              : null,
          anchorDateOverride: row.anchorDateOverride || null,
          triggerModeOverride: row.triggerModeOverride || null,
          mandatoryOverride:
            row.mandatoryOverride === "" ? null : Number(row.mandatoryOverride),
          enabledOverride:
            row.enabledOverride === "" ? null : Number(row.enabledOverride),
          notes: String(row.notes || "").trim() || null,
        };
      })
      .filter((row) => {
        const hasConfig =
          row.recurrenceTypeOverride ||
          row.intervalNOverride != null ||
          (Array.isArray(row.dayOfWeekOverride)
            ? row.dayOfWeekOverride.length > 0
            : row.dayOfWeekOverride != null) ||
          row.dayOfMonthOverride != null ||
          row.anchorDateOverride ||
          row.triggerModeOverride ||
          row.mandatoryOverride != null ||
          row.enabledOverride != null ||
          row.notes;
        return row.taskId && hasConfig;
      });
  };

  const onSave = async () => {
    if (!device) return;
    if (!form.servicePlanTemplateId) {
      setSaveError("Service plan template is required");
      return;
    }
    if (!form.routeTemplateId) {
      setSaveError("Route template is required");
      return;
    }
    if (!form.startsOn) {
      setSaveError("Start date is required");
      return;
    }

    const payload = {
      ...(isRoomTarget
        ? { roomId: Number(device.pk_id) }
        : { deviceId: Number(device.pk_id) }),
      servicePlanTemplateId: Number(form.servicePlanTemplateId),
      routeTemplateId: Number(form.routeTemplateId),
      startsOn: form.startsOn,
      endsOn: form.endsOn || null,
      timezone: form.timezone || "Europe/Copenhagen",
      active: form.active ? 1 : 0,
    };

    try {
      setSaving(true);
      setSaveError(null);
      setSaveSuccess(null);

      let saved;
      if (existingPlan?.pk_id) {
        const result = await updateDeviceServicePlan(
          existingPlan.pk_id,
          payload,
        );
        saved = result?.plan || null;
      } else {
        const result = await createDeviceServicePlan(payload);
        saved = result?.plan || null;
      }

      if (saved) {
        setPlans((prev) => {
          const withoutSaved = prev.filter((p) => p.pk_id !== saved.pk_id);
          return [...withoutSaved, saved];
        });
      }

      const planId = saved?.pk_id || existingPlan?.pk_id;
      if (planId) {
        const overridesPayload = buildOverridePayload();
        await replaceDevicePlanTaskOverrides(planId, overridesPayload);
      }

      setSaveSuccess("Plan assignment saved");
    } catch (err) {
      setSaveError(err.message || "Failed to save plan assignment");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <TopNavBar heading="Configure Plan Allocation" />
      <Container maxWidth="md" sx={{ mt: 3, pb: 5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
          <Tooltip title="Go back">
            <IconButton onClick={() => window.history.back()}>
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>

          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {device?.name ||
              device?.device_name ||
              device?.room_name ||
              `${isRoomTarget ? "Room" : "Device"} #${targetId}`}
          </Typography>

          {device?.rc_device_id && (
            <Chip label={device.rc_device_id} size="small" variant="outlined" />
          )}

          {existingPlan && (
            <Chip
              label={Number(existingPlan.active) === 1 ? "Enabled" : "Disabled"}
              size="small"
              color={Number(existingPlan.active) === 1 ? "success" : "default"}
              variant="outlined"
            />
          )}
        </Box>

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && device && serviceTypeOptions.length > 0 && (
          <Tabs
            value={selectedServiceTypeId}
            onChange={(e, value) => setSelectedServiceTypeId(value)}
            sx={{ mb: 2 }}
          >
            {serviceTypeOptions.map((option) => {
              const hasPlan = plans.some(
                (plan) =>
                  String(plan.service_type_id || "") === String(option.id),
              );
              return (
                <Tab
                  key={option.id}
                  value={option.id}
                  label={
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <span>{option.name}</span>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={hasPlan ? "Assigned" : "Not assigned"}
                        color={hasPlan ? "success" : "default"}
                      />
                    </Stack>
                  }
                />
              );
            })}
          </Tabs>
        )}

        {!loading && !error && device && (
          <Stack spacing={2}>
            {saveError && <Alert severity="error">{saveError}</Alert>}
            {saveSuccess && <Alert severity="success">{saveSuccess}</Alert>}

            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <Stack spacing={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(form.active)}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          active: e.target.checked,
                        }))
                      }
                    />
                  }
                  label="Automatic plan enabled"
                />

                <TextField
                  select
                  label="Service plan template"
                  value={form.servicePlanTemplateId}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      servicePlanTemplateId: e.target.value,
                    }))
                  }
                >
                  <MenuItem value="">Select service plan template</MenuItem>
                  {filteredServiceTemplates.map((tpl) => (
                    <MenuItem key={tpl.pk_id} value={tpl.pk_id}>
                      {tpl.template_name}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  label="Route template"
                  value={form.routeTemplateId}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      routeTemplateId: e.target.value,
                    }))
                  }
                >
                  <MenuItem value="">Select route template</MenuItem>
                  {filteredRouteTemplates.map((tpl) => (
                    <MenuItem key={tpl.pk_id} value={tpl.pk_id}>
                      {tpl.template_name}
                    </MenuItem>
                  ))}
                </TextField>

                <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
                  <TextField
                    type="date"
                    label="Starts on"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={form.startsOn}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        startsOn: e.target.value,
                      }))
                    }
                  />
                  <TextField
                    type="date"
                    label="Ends on (optional)"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={form.endsOn}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        endsOn: e.target.value,
                      }))
                    }
                  />
                </Stack>

                <TextField
                  label="Timezone"
                  value={form.timezone}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      timezone: e.target.value,
                    }))
                  }
                />

                {isRoomTarget && form.servicePlanTemplateId && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Tasks for this room
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      All non-device tasks from the template can be allocated to
                      this room. None are allocated by default - check a task to
                      add it, then configure its schedule in "Configure task
                      schedule" below.
                    </Typography>
                    <Stack spacing={0.25}>
                      <Stack
                        direction="row"
                        spacing={2}
                        sx={{ pl: 3, pr: 1, pt: 0.5, pb: 1 }}
                      >
                        {splitIntoColumns(eligibleTemplateTasks).map(
                          (column, columnIndex) => (
                            <Box
                              key={columnIndex}
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                flex: 1,
                                minWidth: 0,
                              }}
                            >
                              {column.map((task) => {
                                const taskId = String(
                                  task.task_id || task.pk_id || "",
                                );
                                const overrideRow = overrideDraft.find(
                                  (row) => String(row.taskId) === taskId,
                                );
                                const isChecked =
                                  overrideRow?.enabledOverride === "1";
                                return (
                                  <FormControlLabel
                                    key={taskId}
                                    control={
                                      <Switch
                                        checked={isChecked}
                                        onChange={() =>
                                          toggleRoomTaskEnabled(taskId)
                                        }
                                      />
                                    }
                                    label={task.task_name || `Task #${taskId}`}
                                  />
                                );
                              })}
                            </Box>
                          ),
                        )}
                      </Stack>
                      {eligibleTemplateTasks.length === 0 && (
                        <Typography variant="body2" color="text.disabled">
                          This template has no non-device tasks.
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                )}

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ alignSelf: "center" }}
                  >
                    Base assignment and overrides are saved together.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={onSave}
                    disabled={saving}
                  >
                    {saving
                      ? "Saving..."
                      : existingPlan
                        ? "Update Assignment"
                        : "Create Assignment"}
                  </Button>
                </Box>

                <Divider />

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{ fontSize: 16, fontWeight: 700 }}
                  >
                    {isRoomTarget
                      ? "Configure task schedule"
                      : "Task Overrides"}
                  </Typography>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`${overrideDraft.length} rows`}
                  />
                  <Box sx={{ ml: "auto" }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={addOverrideRow}
                    >
                      {isRoomTarget ? "Add Schedule" : "Add Override"}
                    </Button>
                  </Box>
                </Box>

                {overridesLoading && (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 1 }}
                  >
                    <CircularProgress size={20} />
                  </Box>
                )}

                {!overridesLoading && overrideDraft.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No task overrides configured.
                  </Typography>
                )}

                {!overridesLoading &&
                  overrideDraft.map((row) => (
                    <Paper
                      key={row.id}
                      variant="outlined"
                      sx={{ p: 1.5, borderRadius: 1.5 }}
                    >
                      <Stack spacing={1.25}>
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block" }}
                          >
                            Current task details
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {taskById.get(String(row.taskId))?.task_name ||
                              `Task #${row.taskId || "?"}`}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block" }}
                          >
                            {formatTaskSummary(
                              taskById.get(String(row.taskId)),
                            )}
                          </Typography>
                        </Box>

                        <Stack
                          direction={{ xs: "column", md: "row" }}
                          spacing={1.25}
                        >
                          <TextField
                            select
                            fullWidth
                            label="Task"
                            value={row.taskId}
                            onChange={(e) =>
                              updateOverrideRow(row.id, {
                                taskId: e.target.value,
                              })
                            }
                          >
                            <MenuItem value="">Select task</MenuItem>
                            {taskOptions.map((option) => {
                              const usedByOtherRow = overrideDraft.some(
                                (existing) =>
                                  existing.id !== row.id &&
                                  String(existing.taskId || "") ===
                                    String(option.id),
                              );
                              return (
                                <MenuItem
                                  key={option.id}
                                  value={option.id}
                                  disabled={usedByOtherRow}
                                >
                                  {option.name}
                                </MenuItem>
                              );
                            })}
                          </TextField>

                          <TextField
                            select
                            fullWidth
                            label="Enabled override"
                            value={row.enabledOverride}
                            onChange={(e) =>
                              updateOverrideRow(row.id, {
                                enabledOverride: e.target.value,
                              })
                            }
                          >
                            <MenuItem value="">No override</MenuItem>
                            <MenuItem value="1">Enabled</MenuItem>
                            <MenuItem value="0">Disabled</MenuItem>
                          </TextField>

                          {/* <TextField
                            select
                            fullWidth
                            label="Mandatory override"
                            value={row.mandatoryOverride}
                            onChange={(e) =>
                              updateOverrideRow(row.id, {
                                mandatoryOverride: e.target.value,
                              })
                            }
                          >
                            <MenuItem value="">No override</MenuItem>
                            <MenuItem value="1">Mandatory</MenuItem>
                            <MenuItem value="0">Optional</MenuItem>
                          </TextField> */}
                        </Stack>

                        <Stack
                          direction={{ xs: "column", md: "row" }}
                          spacing={1.25}
                        >
                          <TextField
                            select
                            fullWidth
                            label="Recurrence override"
                            value={row.recurrenceTypeOverride}
                            onChange={(e) =>
                              updateOverrideRow(row.id, {
                                recurrenceTypeOverride: e.target.value,
                                intervalNOverride:
                                  e.target.value === "every_n_days"
                                    ? row.intervalNOverride
                                    : "",
                                dayOfWeekOverride:
                                  e.target.value === "weekly"
                                    ? row.dayOfWeekOverride
                                    : [],
                                dayOfMonthOverride:
                                  e.target.value === "monthly"
                                    ? row.dayOfMonthOverride
                                    : "",
                              })
                            }
                          >
                            <MenuItem value="">No override</MenuItem>
                            {RECURRENCE_OPTIONS.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </TextField>

                          {row.recurrenceTypeOverride === "every_n_days" && (
                            <TextField
                              type="number"
                              fullWidth
                              label="Interval N"
                              inputProps={{ min: 1 }}
                              value={row.intervalNOverride}
                              onChange={(e) =>
                                updateOverrideRow(row.id, {
                                  intervalNOverride: e.target.value,
                                })
                              }
                            />
                          )}

                          {row.recurrenceTypeOverride === "weekly" && (
                            <TextField
                              select
                              fullWidth
                              label="Weekdays"
                              value={row.dayOfWeekOverride}
                              SelectProps={{
                                multiple: true,
                                renderValue: (selected) =>
                                  (Array.isArray(selected) ? selected : [])
                                    .slice()
                                    .sort((a, b) => Number(a) - Number(b))
                                    .map(
                                      (value) =>
                                        WEEKDAY_LABEL_BY_VALUE.get(
                                          String(value),
                                        ) || value,
                                    )
                                    .join(", "),
                              }}
                              onChange={(e) =>
                                updateOverrideRow(row.id, {
                                  dayOfWeekOverride: e.target.value,
                                })
                              }
                            >
                              {WEEKDAY_OPTIONS.map((option) => (
                                <MenuItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  <Checkbox
                                    checked={row.dayOfWeekOverride.includes(
                                      option.value,
                                    )}
                                  />
                                  <ListItemText primary={option.label} />
                                </MenuItem>
                              ))}
                            </TextField>
                          )}

                          {row.recurrenceTypeOverride === "monthly" && (
                            <TextField
                              type="number"
                              fullWidth
                              label="Day of month"
                              inputProps={{ min: 1, max: 31 }}
                              value={row.dayOfMonthOverride}
                              onChange={(e) =>
                                updateOverrideRow(row.id, {
                                  dayOfMonthOverride: e.target.value,
                                })
                              }
                            />
                          )}

                          {/* <TextField
                            select
                            fullWidth
                            label="Trigger override"
                            value={row.triggerModeOverride}
                            onChange={(e) =>
                              updateOverrideRow(row.id, {
                                triggerModeOverride: e.target.value,
                              })
                            }
                          >
                            <MenuItem value="">No override</MenuItem>
                            {TRIGGER_MODE_OPTIONS.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </TextField> */}
                        </Stack>

                        <Stack
                          direction={{ xs: "column", md: "row" }}
                          spacing={1.25}
                        >
                          <TextField
                            type="date"
                            fullWidth
                            label="Anchor date override"
                            InputLabelProps={{ shrink: true }}
                            value={row.anchorDateOverride}
                            onChange={(e) =>
                              updateOverrideRow(row.id, {
                                anchorDateOverride: e.target.value,
                              })
                            }
                          />

                          <TextField
                            fullWidth
                            label="Notes"
                            value={row.notes}
                            onChange={(e) =>
                              updateOverrideRow(row.id, {
                                notes: e.target.value,
                              })
                            }
                          />

                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "flex-end",
                            }}
                          >
                            <Button
                              color="error"
                              startIcon={<DeleteOutlineIcon />}
                              onClick={() => removeOverrideRow(row.id)}
                            >
                              Remove
                            </Button>
                          </Box>
                        </Stack>
                      </Stack>
                    </Paper>
                  ))}
              </Stack>
            </Paper>
          </Stack>
        )}
      </Container>
    </>
  );
}
